/* ═══════════════════════════════════════════════════════════════
   The MODULES toolbox — what this spec already carries, and the one door
   to the library (docs/MASTERS_UNIFIED_PLAN.md Phase 5; rebuilt by plan
   Spec Tighten F3).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a
   variant (a variant is a client version of a master).

   ONE body, hosted by both editors: the Spec Masters Studio (masters and
   client variants) and the Designer (project specs) each dock it as a
   panel, so inserting a module is the same gesture wherever you are.

   The body is ONE list, "In this document": a slim row per inserted module
   (name · version · update badge; click = select its blocks, "…" = Update /
   Remove). The library lives behind the top button, "Insert module…", in a
   dialog that hosts the very same `ModuleBrowser` the Module Library page
   uses. Dragging a module out of the toolbox is retired (plan F3c).

   Presentational on purpose — every decision (what matched, what landed,
   what still needs placing) belongs to `useModuleInsert`.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Layers, MoreHorizontal, Plus, X } from 'lucide-react';
import { Icon } from './ui/Icon';
import { Button } from './ui/button';
import { Hint } from './ui/tooltip';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from './ui/context-menu';
import type { Column } from './DataTable';
import EmptyState from './EmptyState';
import { ModuleBrowser } from './ModuleBrowser';
import { ModulePreview } from './ModulePreview';
import { rem } from '../lib/rem';
import { Prefs } from '../services/prefsService';
import type { SpecModule } from '../../shared/spec-modules';
import { moduleCountLabel, modulePreviewAnchors } from '../../shared/spec-modules';
import { StatusBadge } from './StatusBadge';
import type { DocModule, PendingChunk } from './useModuleInsert';

/** Same preference the Module Library page's Preview toggle writes — one
 *  "show the preview pane" setting per user across both surfaces. */
const PREF_PREVIEW_SHOWN = 'module-library.previewShown';

/** View-mode guard (Spec Studio). While `inert`, the edit-only controls stay
 *  focusable with aria-disabled, explain themselves, and call `onBlocked`
 *  (the Edit-button nudge) instead of acting. Absent ⇒ native disabled, as ever. */
export interface InsertGuard {
  inert: boolean;
  reason: string;
  onBlocked: () => void;
}

export interface ModulesPanelProps {
  modules: SpecModule[];
  loading: boolean;
  error: string | null;
  /** The target's own edit gate (specs.edit_* / project_specs.edit_*). */
  canInsert: boolean;
  /** Why inserting is off, when it is — shown, never silently disabled. */
  insertHint?: string;
  insertGuard?: InsertGuard;
  busyModuleId: string | null;
  onInsert: (module: SpecModule) => void;
  onRetry: () => void;
  /** Who is browsing — for category create/rename/move/delete activity.
   *  `userPermissions` absent ⇒ category MANAGEMENT is off here (browsing
   *  and inserting still work); only a host that already threads the full
   *  permission catalog (Spec Studio) passes it. */
  userName?: string;
  userPermissions?: string[];
  toast?: (msg: string, isError?: boolean) => void;

  // ── The placement tray ──
  chips: PendingChunk[];
  armedKey: string | null;
  onArm: (key: string) => void;
  onDiscard: (key: string) => void;

  // ── "In this document" (R2 Phase 2) ──
  /** Every module standing in the open spec. Absent ⇒ treated as empty. */
  inDocument?: DocModule[];
  /** Scroll to and select the insertion's blocks. */
  onReveal?: (insertId: string) => void;
  /** Delete every block of the insertion (confirmed by the caller). */
  onRemove?: (insertId: string) => void;
  /** Open the library update for this insertion. Absent ⇒ no update state is
   *  shown. The rect is where the badge was clicked, so a host with an Updates
   *  panel can pop it next to the badge. */
  onReview?: (insertId: string, anchor: DOMRect) => void;
  /** WHOLE-MODULE mode (the Designer, which has no Updates panel): the offer
   *  the user opened, answered right here. ⛔ Ruling 3 — a spec is never
   *  silent about the library moving, even where nothing can ever PR into it. */
  reviewFor?: {
    insertId: string;
    entryId: string;
    moduleName: string;
    fromVersion: number;
    toVersion: number;
    count: number;
  } | null;
  onReviewDecide?: (decision: 'accepted' | 'rejected') => void;
  onReviewCancel?: () => void;

  /** A name tag in the document was clicked — show this insertion's row. */
  revealInsertId?: string | null;
  /** Changes on every reveal request, so the same row can be re-revealed. */
  revealSeq?: number;
}

const UPDATE_BADGE: Record<'available' | 'dismissed', { label: string; title: string }> = {
  available: {
    label: 'Update available',
    title: 'The library has a newer version — review it',
  },
  dismissed: {
    label: 'Update dismissed',
    title: 'You kept this copy at the last offer — click to look again',
  },
};

/** One module standing in the open document — a single slim row. */
function DocModuleRow({
  entry,
  canEdit,
  editHint,
  guard,
  onReveal,
  onRemove,
  onReview,
  review,
  onReviewDecide,
  onReviewCancel,
  revealed,
  revealSeq,
}: {
  entry: DocModule;
  canEdit: boolean;
  editHint?: string;
  guard?: InsertGuard;
  onReveal?: (insertId: string) => void;
  onRemove?: (insertId: string) => void;
  onReview?: (insertId: string, anchor: DOMRect) => void;
  review?: ModulesPanelProps['reviewFor'];
  onReviewDecide?: (decision: 'accepted' | 'rejected') => void;
  onReviewCancel?: () => void;
  /** This row was asked for by a click on its name tag in the document. */
  revealed?: boolean;
  revealSeq?: number;
}) {
  const rowRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (revealed) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [revealed, revealSeq]);
  const name = entry.occurrences
    ? `${entry.moduleName} (${entry.occurrence} of ${entry.occurrences})`
    : entry.moduleName;
  const update = entry.update === 'none' ? null : UPDATE_BADGE[entry.update];
  const tooltip = [
    name,
    `v${entry.moduleVersion}`,
    `${entry.blockCount} block${entry.blockCount === 1 ? '' : 's'}`,
    entry.state === 'modified' ? 'edited in this spec' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const canUpdate = !!update && !!onReview;
  const blocked = !!guard?.inert;
  const nativeOff = !canEdit && !blocked;

  return (
    <div ref={rowRef} role="listitem" style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          minHeight: rem(36),
          padding: `0 var(--space-1) 0 var(--space-2)`,
          borderRadius: 'var(--radius-md)',
          background: revealed ? 'rgba(var(--acr), 0.14)' : 'transparent',
        }}
      >
        <button
          type="button"
          onClick={() => onReveal?.(entry.insertId)}
          title={`${tooltip} — click to select these blocks in the document`}
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: rem(32),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
            color: 'var(--tx)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <span
            style={{
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 600,
            }}
          >
            {name}
          </span>
          <span style={{ flexShrink: 0, color: 'var(--tm)', fontSize: 'var(--font-size-xs)' }}>
            v{entry.moduleVersion}
          </span>
        </button>

        {update && onReview && (
          <button
            type="button"
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              minHeight: rem(32),
              display: 'flex',
              alignItems: 'center',
            }}
            title={update.title}
            onClick={(e) => onReview(entry.insertId, e.currentTarget.getBoundingClientRect())}
          >
            <StatusBadge
              status="module_update"
              label={
                entry.pendingUpdates > 0
                  ? `${update.label} (${entry.pendingUpdates})`
                  : update.label
              }
              variant={entry.update === 'available' ? 'info' : 'neutral'}
              style={{ textTransform: 'none', letterSpacing: 0 }}
            />
          </button>
        )}

        <ContextMenu>
          <ContextMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Actions for ${name}`}
              title="Update or remove"
              style={{ flexShrink: 0 }}
            >
              <Icon icon={MoreHorizontal} size="sm" aria-hidden="true" />
            </Button>
          </ContextMenuTrigger>
          <ContextMenuContent align="end" className="dsg-menu">
            <ContextMenuItem
              className="dsg-menu-item"
              disabled={!canUpdate}
              onSelect={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                onReview?.(entry.insertId, rect);
              }}
            >
              {canUpdate ? 'Update' : 'Update (already current)'}
            </ContextMenuItem>
            <ContextMenuSeparator className="dsg-menu-sep" />
            <ContextMenuItem
              className={blocked ? 'dsg-menu-item' : 'dsg-menu-item is-danger'}
              disabled={nativeOff}
              aria-disabled={blocked || undefined}
              title={
                blocked ? guard?.reason : canEdit ? undefined : editHint || 'This spec is read-only'
              }
              onSelect={(e) => {
                if (blocked) {
                  e.preventDefault();
                  guard?.onBlocked();
                  return;
                }
                onRemove?.(entry.insertId);
              }}
              style={blocked ? { color: 'var(--color-text-muted)' } : undefined}
            >
              Remove from this spec
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </div>

      {review && review.insertId === entry.insertId && (
        <div
          role="group"
          aria-label="Library update"
          style={{
            display: 'grid',
            gap: 'var(--space-2)',
            padding: 'var(--space-2)',
            margin: `0 var(--space-1) var(--space-1)`,
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--sa)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
          }}
        >
          <p style={{ margin: 0 }}>
            The library moved from v{review.fromVersion} to v{review.toVersion}.{' '}
            {review.count === 1 ? '1 paragraph differs' : `${review.count} paragraphs differ`} from
            this copy.
          </p>
          <p style={{ margin: 0 }}>
            Paragraph-by-paragraph review lives in the Master Specs Studio — here the module updates
            as a whole.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            <Button variant="ghost" size="sm" onClick={() => onReviewCancel?.()}>
              Not now
            </Button>
            <Hint text={blocked ? guard?.reason : null}>
              <Button
                variant="outline"
                size="sm"
                disabled={nativeOff}
                aria-disabled={blocked || undefined}
                className={blocked ? 'sps-vm-inert' : undefined}
                title={
                  blocked
                    ? undefined
                    : canEdit
                      ? 'Keep this copy as it is — the next version of the module will ask again'
                      : editHint || 'This spec is read-only'
                }
                onClick={() => (blocked ? guard?.onBlocked() : onReviewDecide?.('rejected'))}
              >
                Keep mine
              </Button>
            </Hint>
            <Hint text={blocked ? guard?.reason : null}>
              <Button
                size="sm"
                disabled={nativeOff}
                aria-disabled={blocked || undefined}
                className={blocked ? 'sps-vm-inert' : undefined}
                title={
                  blocked
                    ? undefined
                    : canEdit
                      ? 'Replace this insertion with the library version'
                      : editHint || 'This spec is read-only'
                }
                onClick={() => (blocked ? guard?.onBlocked() : onReviewDecide?.('accepted'))}
              >
                Take the update
              </Button>
            </Hint>
          </div>
        </div>
      )}
    </div>
  );
}

export function ModulesPanel({
  modules,
  loading,
  error,
  canInsert,
  insertHint,
  insertGuard,
  busyModuleId,
  onInsert,
  onRetry,
  userName,
  userPermissions,
  toast,
  chips,
  armedKey,
  onArm,
  onDiscard,
  inDocument,
  onReveal,
  onRemove,
  onReview,
  reviewFor,
  onReviewDecide,
  onReviewCancel,
  revealInsertId,
  revealSeq,
}: ModulesPanelProps) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [previewShown, setPreviewShownState] = React.useState<boolean>(() => {
    const saved = Prefs.get(PREF_PREVIEW_SHOWN);
    return typeof saved === 'boolean' ? saved : false;
  });
  const setPreviewShown = (v: boolean) => {
    setPreviewShownState(v);
    Prefs.set(PREF_PREVIEW_SHOWN, v);
  };
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const modsRef = React.useRef({ ctrl: false, shift: false });
  const previewed = modules.find((m) => m.id === previewId) || null;
  const docList = inDocument ?? [];

  const doInsert = (m: SpecModule) => {
    if (!canInsert || busyModuleId === m.id) return;
    onInsert(m);
    setPickerOpen(false);
  };

  // Plain click inserts; Ctrl / Shift-click previews instead (same modifier
  // meaning as the Module Library page, where plain click opens the module).
  const onPickerRowClick = (m: SpecModule) => {
    const { ctrl, shift } = modsRef.current;
    if (ctrl || shift) {
      setPreviewId(m.id);
      return;
    }
    doInsert(m);
  };

  const browserColumns: Column<SpecModule>[] = React.useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        width: 260,
        getValue: (m) => m.name || '',
        render: (m) => {
          const anchors = modulePreviewAnchors(m);
          return (
            <span
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}
            >
              <span className="sf-mod-row-text" style={{ flex: 1, minWidth: 0 }}>
                <span className="sf-mod-row-name" title={m.name}>
                  {m.name}
                </span>
                <span className="sf-mod-row-meta">{moduleCountLabel(m)}</span>
                {anchors.map((a, i) => (
                  <span key={i} className="sf-mod-row-anchor">
                    {a}
                  </span>
                ))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!canInsert || busyModuleId === m.id}
                onClick={(e) => {
                  e.stopPropagation();
                  doInsert(m);
                }}
                title={
                  canInsert
                    ? `Insert a copy of “${m.name}” into this spec`
                    : insertHint || 'You do not have permission to edit this spec'
                }
              >
                {busyModuleId === m.id ? 'Inserting…' : 'Insert'}
              </Button>
            </span>
          );
        },
      },
      // Hidden by default — never rendered as a column, but the table's own
      // search reads these values, so searching a CSI number or a word from
      // the description matches.
      {
        key: 'description',
        label: 'Description',
        width: 200,
        getValue: (m) => m.description || '',
        defaultHidden: true,
      },
      {
        key: 'sourceCsi',
        label: 'Source CSI',
        width: 120,
        getValue: (m) => m.sourceCsi || '',
        defaultHidden: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canInsert, busyModuleId, onInsert, insertHint]
  );

  return (
    <div className="dsg-pal sf-mod-pal">
      {/* ── Chunks that could not place themselves ── */}
      {chips.length > 0 && (
        <div
          className="sf-mod-tray sf-mod-section-scroll"
          style={{ flexShrink: 0, maxHeight: rem(220) }}
          role="group"
          aria-label="Chunks that need a spot"
        >
          <h3 className="sf-mod-head">
            {chips.length} chunk{chips.length === 1 ? '' : 's'} need
            {chips.length === 1 ? 's' : ''} a spot
          </h3>
          <p className="sf-mod-hint">
            {armedKey
              ? 'Click in the document where it should go. Esc cancels.'
              : 'Click a chunk, then click where it goes in the document.'}
          </p>
          {chips.map((chip) => (
            <div
              key={chip.key}
              className={`sf-mod-chip${armedKey === chip.key ? ' is-armed' : ''}`}
            >
              <button
                type="button"
                className="sf-mod-chip-main"
                aria-pressed={armedKey === chip.key}
                onClick={() => onArm(chip.key)}
                title={
                  chip.reason === 'ambiguous'
                    ? 'This spec has more than one place that fits — click here, then click where it goes'
                    : 'Nothing in this spec matches where this chunk was standing — click here, then click where it goes'
                }
              >
                <span className="sf-mod-chip-name">{chip.moduleName}</span>
                <span className="sf-mod-chip-meta">
                  {chip.blockCount} block{chip.blockCount === 1 ? '' : 's'} · {chip.label}
                </span>
              </button>
              <button
                type="button"
                className="sf-mod-chip-x"
                aria-label={`Discard the ${chip.moduleName} chunk`}
                title="Discard this chunk"
                onClick={() => onDiscard(chip.key)}
              >
                <Icon icon={X} size="md" aria-hidden="true" />
              </button>
            </div>
          ))}
          <p className="sf-mod-hint sf-mod-tray-warn">
            These are not saved. Leaving this spec discards anything still unplaced.
          </p>
        </div>
      )}

      {/* ── The toolbox: one door to the library, one list of what is here ── */}
      <div style={{ flexShrink: 0, padding: 'var(--space-2)' }}>
        <Hint text={insertGuard?.inert ? insertGuard.reason : null}>
          <Button
            size="sm"
            style={{ width: '100%' }}
            disabled={!canInsert && !insertGuard?.inert}
            aria-disabled={insertGuard?.inert || undefined}
            className={insertGuard?.inert ? 'sps-vm-inert' : undefined}
            title={
              canInsert || insertGuard?.inert
                ? undefined
                : insertHint || 'You do not have permission to edit this spec'
            }
            onClick={() => (insertGuard?.inert ? insertGuard.onBlocked() : setPickerOpen(true))}
          >
            <Icon icon={Plus} size="sm" aria-hidden="true" /> Insert module…
          </Button>
        </Hint>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
          padding: `0 var(--space-2) var(--space-2)`,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--tx)',
          }}
        >
          In this document{docList.length ? ` (${docList.length})` : ''}
        </h3>
        {docList.length === 0 ? (
          <EmptyState
            compact
            icon={<Icon icon={Layers} em={2.1538} aria-hidden="true" />}
            title="No modules in this document"
            subtitle={canInsert ? undefined : (insertHint ?? insertGuard?.reason)}
            action={
              canInsert
                ? { label: 'Insert module…', onClick: () => setPickerOpen(true) }
                : undefined
            }
          />
        ) : (
          <div role="list" style={{ display: 'flex', flexDirection: 'column' }}>
            {docList.map((entry) => (
              <DocModuleRow
                key={entry.insertId}
                entry={entry}
                canEdit={canInsert}
                {...(insertHint ? { editHint: insertHint } : {})}
                {...(insertGuard ? { guard: insertGuard } : {})}
                {...(onReveal ? { onReveal } : {})}
                {...(onRemove ? { onRemove } : {})}
                {...(onReview ? { onReview } : {})}
                {...(reviewFor ? { review: reviewFor } : {})}
                {...(onReviewDecide ? { onReviewDecide } : {})}
                {...(onReviewCancel ? { onReviewCancel } : {})}
                revealed={!!revealInsertId && revealInsertId === entry.insertId}
                {...(revealSeq != null ? { revealSeq } : {})}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Insert module… — the library, same browser as the Module Library page ── */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Insert module</DialogTitle>
            <DialogDescription>
              Click a module to insert a copy into this spec — each article lands at the end of its
              own Part, or joins the same-named article already in that Part. Articles with no Part
              go after the block your cursor is in. Ctrl-click to preview one first. The copy is
              tagged with the module and its version; editing it never changes the library.
            </DialogDescription>
          </DialogHeader>
          <div
            style={{ display: 'flex', height: '65vh', minHeight: 0, gap: 'var(--space-3)' }}
            onMouseDownCapture={(e) => {
              modsRef.current = { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey };
            }}
          >
            <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {loading ? (
                <p role="status" className="sf-mod-hint">
                  Loading the module library…
                </p>
              ) : error ? (
                <EmptyState
                  icon={<Icon icon={Layers} em={2.1538} aria-hidden="true" />}
                  title="The module library could not be loaded"
                  subtitle={error}
                  action={{ label: 'Try again', onClick: onRetry }}
                />
              ) : (
                <ModuleBrowser
                  hostId="insert"
                  layout="stacked"
                  modules={modules}
                  userName={userName || ''}
                  userPermissions={userPermissions || []}
                  toast={toast || (() => {})}
                  storageKeyBase="module-insert-table.v1"
                  columns={browserColumns}
                  onRowClick={onPickerRowClick}
                  selectable={false}
                  searchPlaceholder="Search modules, categories, tags…"
                  toolbarExtra={
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        minHeight: rem(32),
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--tx)',
                      }}
                    >
                      Preview
                      <Switch
                        checked={previewShown}
                        onCheckedChange={setPreviewShown}
                        aria-label="Show module preview"
                      />
                    </label>
                  }
                />
              )}
            </div>
            {previewShown && (
              <aside
                aria-label="Module preview"
                style={{
                  width: rem(300),
                  flexShrink: 0,
                  borderLeft: '1px solid var(--bd)',
                  overflow: 'auto',
                  padding: 'var(--space-3)',
                }}
              >
                {previewed ? (
                  <ModulePreview module={previewed} />
                ) : (
                  <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                    Ctrl-click a module to preview the blocks it carries here.
                  </div>
                )}
              </aside>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
